import {
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Link,
} from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { PROFILES_INFO } from '../utils';
import StarIcon from '../StarIcon';
import { AskForInfoCard, RedLinkButton, ProfileBaseInfoCard } from '../styles';
import { useMatchMakingStore } from '../store';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export const ProfileHeader = ({ profile }) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const intl = useIntl();
  const i18n = translations.MatchMakingV2(intl).USERS;
  const locale = useMatchMakingStore((state) => state.locale);

  return (
    <Stack
      alignItems={'center'}
      justifyContent={'center'}
      gap={isSmDown ? '5px' : '15px'}
    >
      <Stack
        alignItems={'center'}
        justifyContent={'center'}
        width={'80px'}
        height={'80px'}
      >
        <StarIcon color={profile.iconColor} />
      </Stack>
      <Typography variant="xl">
        {locale === 'en' ? (
          <>
            <strong style={{ textTransform: 'uppercase' }}>
              {i18n[profile.i18nLabel]}
            </strong>{' '}
            {i18n.profile}
          </>
        ) : (
          <>
            {i18n.profile}{' '}
            <strong style={{ textTransform: 'uppercase' }}>
              {i18n[profile.i18nLabel]}
            </strong>
          </>
        )}
      </Typography>
      {profile !== PROFILES_INFO.BASIC ? (
        <Typography variant="body1">
          {i18n.subtitle} {profile.maxUsers} {i18n.subtitleUsers}
        </Typography>
      ) : (
        <Typography variant="body1" textAlign={'center'}>
          {i18n.basicSubOne} <br /> {i18n.basicSubThree} <br />{' '}
          {i18n.basicSubFour} <br /> {i18n.basicSubFive} <br />{' '}
          {i18n.basicSubTwo}
        </Typography>
      )}
    </Stack>
  );
};

export const ButtonsContainer = ({
  profile,
  onAskInformation = null,
  onSwitchToGold = null,
  onCreateUser = null,
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const intl = useIntl();
  const i18n = translations.MatchMakingV2(intl).USERS;

  return profile !== PROFILES_INFO.BASIC ? (
    <Stack
      flexDirection={isSmDown ? 'column' : 'row'}
      gap={isSmDown ? '20px' : '25px'}
      justifyContent={'center'}
    >
      {profile === PROFILES_INFO.SILVER ? (
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          onClick={() => onSwitchToGold?.()}
        >
          {i18n.buttons.switch} ({PROFILES_INFO.GOLD.maxUsers}{' '}
          {i18n.subtitleUsers})
        </Button>
      ) : null}
      <Button
        variant="contained"
        color="primary"
        size="medium"
        onClick={() => onCreateUser?.()}
      >
        {i18n.buttons.create}
      </Button>
    </Stack>
  ) : (
    <Stack
      flexDirection={isSmDown ? 'column' : 'row'}
      gap={isSmDown ? '20px' : '40px'}
      justifyContent={'center'}
      alignItems={'center'}
    >
      <AskForInfoCard>
        <Stack gap={'5px'}>
          <Stack gap={'5px'} flexDirection={'row'} alignItems={'center'}>
            <Typography variant="m-bold">
              {i18n.profile}{' '}
              <span style={{ textTransform: 'uppercase' }}>{i18n.silver}</span>
            </Typography>
            <Stack
              alignItems={'center'}
              justifyContent={'center'}
              width={'24px'}
              height={'24px'}
              paddingBottom={'4px'}
            >
              <StarIcon color={PROFILES_INFO.SILVER.iconColor} />
            </Stack>
          </Stack>
          <Typography variant="body2">
            {PROFILES_INFO.SILVER.maxUsers} {i18n.matchmakingUsers}
          </Typography>
        </Stack>
        <RedLinkButton
          msg={i18n.buttons.ask}
          onClick={() => onAskInformation?.(PROFILES_INFO.SILVER)}
        />
      </AskForInfoCard>
      <AskForInfoCard>
        <Stack gap={'5px'}>
          <Stack gap={'5px'} flexDirection={'row'} alignItems={'center'}>
            <Typography variant="m-bold">
              {i18n.profile}{' '}
              <span style={{ textTransform: 'uppercase' }}>{i18n.gold}</span>
            </Typography>
            <Stack
              alignItems={'center'}
              justifyContent={'center'}
              width={'24px'}
              height={'24px'}
              paddingBottom={'4px'}
            >
              <StarIcon color={PROFILES_INFO.GOLD.iconColor} />
            </Stack>
          </Stack>
          <Typography variant="body2">
            {PROFILES_INFO.GOLD.maxUsers} {i18n.matchmakingUsers}
          </Typography>
        </Stack>
        <RedLinkButton
          msg={i18n.buttons.ask}
          onClick={() => onAskInformation?.(PROFILES_INFO.GOLD)}
        />
      </AskForInfoCard>
    </Stack>
  );
};

export const ProfileContent = ({
  profile,
  onAskInformation,
  currentProfile,
  onCreate,
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const intl = useIntl();
  const i18n = translations.MatchMakingV2(intl).USERS;
  const extraMatchmaking = profile.services['Utenze Matchmaking Extra'];

  return (
    <Stack
      alignItems={'center'}
      justifyContent={'center'}
      gap={isSmDown ? '15px' : '30px'}
    >
      <Stack
        alignItems={'center'}
        justifyContent={'center'}
        gap={isSmDown ? '5px' : '15px'}
      >
        <Typography
          variant="title2"
          textAlign={'center'}
          textTransform={'uppercase'}
        >
          {i18n.profile} {currentProfile.i18nLabel}
        </Typography>
        {currentProfile.id === PROFILES_INFO.PREMIUM.id ? (
          <Typography variant="subheadlingMini" textAlign={'center'}>
            {i18n.premiumSubtitleFirst} {extraMatchmaking}{' '}
            {i18n.premiumSubtitleSecond}
          </Typography>
        ) : (
          <Typography variant="subheadlingMini" textAlign={'center'}>
            {i18n.basicSubtitle}
            {profile.email}
          </Typography>
        )}
      </Stack>
      {currentProfile.id === PROFILES_INFO.PREMIUM.id ? (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => onCreate?.()}
          sx={{
            fontSize: '16px',
            lineHeight: '24px',
            ...(isSmDown && {
              width: '100%',
            }),
          }}
        >
          {i18n.buttons.create}
        </Button>
      ) : (
        <ProfileBaseInfoCard>
          <InfoOutlinedIcon sx={{ color: '#333' }} />
          <Stack gap={'5px'}>
            <Typography variant="body" sx={{ fontWeight: 800 }}>
              {i18n.basicInfoTitle}
            </Typography>
            <Typography variant="body">
              {i18n.basicInfoText}
              <Link
                variant="body"
                underline="always"
                sx={{
                  color: theme.palette.primary.main,
                  ml: 0.5,
                }}
                onClick={() => onAskInformation?.(PROFILES_INFO.BASIC)}
              >
                digitalinfo@salonemilano.it
              </Link>
            </Typography>
          </Stack>
        </ProfileBaseInfoCard>
      )}
    </Stack>
  );
};
